import React, { useState } from 'react';
import api from '../../lib/api';
import { Badge } from './badge';
import { Button } from './button';
import { toast } from 'sonner';
import { Package, MapPin, ChevronLeft, X } from 'lucide-react';

/**
 * BinArticleSelector - Shows articles in a BIN position, cabinet, or shelf for selection.
 * Used when a BIN/CAB/SHELF QR code is scanned.
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the selector is visible
 * @param {Function} props.onSelect - Called with article object when user selects one
 * @param {Function} props.onClose - Called when user cancels
 * @param {string} props.binCode - The scanned code (id or code)
 * @param {string} props.scanType - 'bin', 'cab', or 'shelf'
 */
export const BinArticleSelector = ({ open, onSelect, onClose, binCode, scanType = 'bin' }) => {
  const [loading, setLoading] = useState(false);
  const [binData, setBinData] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [articles, setArticles] = useState([]);

  React.useEffect(() => {
    if (open && binCode) {
      if (scanType === 'bin') {
        loadBinStock(binCode);
      } else {
        loadLocationStock(binCode, scanType);
      }
    }
    if (!open) {
      setBinData(null);
      setLocationData(null);
      setArticles([]);
    }
  }, [open, binCode, scanType]);

  const loadBinStock = async (code) => {
    setLoading(true);
    try {
      const res = await api.get(`/bins/${encodeURIComponent(code)}/scan-stock`);
      const data = res.data;
      setBinData(data.bin);
      setArticles(data.articles || []);

      if (data.articles_count === 0) {
        toast.error(`Pozitia ${data.bin.cabinet} > ${data.bin.shelf} > ${data.bin.position} este goala`);
        onClose?.();
        return;
      }

      if (data.articles_count === 1) {
        const art = data.articles[0];
        toast.success(`Articol unic: ${art.article_name}`);
        onSelect?.(art);
        return;
      }
    } catch {
      toast.error(`Pozitie BIN negasita: ${code}`);
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  const loadLocationStock = async (code, type) => {
    setLoading(true);
    try {
      let cabinet = '', shelf = '';
      if (type === 'cab') {
        cabinet = code;
      } else if (type === 'shelf') {
        // Format: CABINET_SHELF (e.g., DU01_RA01)
        const idx = code.lastIndexOf('_');
        if (idx > 0) {
          cabinet = code.substring(0, idx);
          shelf = code.substring(idx + 1);
        } else {
          cabinet = code;
        }
      }
      const params = new URLSearchParams({ cabinet });
      if (shelf) params.append('shelf', shelf);
      const res = await api.get(`/bins-scan-location?${params.toString()}`);
      const data = res.data;
      setLocationData(data.location);
      setArticles(data.articles || []);

      if (data.articles_count === 0) {
        toast.error(`${data.location.label} - niciun articol cu stoc`);
        onClose?.();
        return;
      }

      if (data.articles_count === 1) {
        const art = data.articles[0];
        toast.success(`Articol unic: ${art.article_name}`);
        onSelect?.(art);
        return;
      }
    } catch {
      toast.error(`Locatie negasita: ${code}`);
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  if (!open || articles.length <= 1) return null;

  const headerLabel = binData
    ? `${binData.cabinet} > ${binData.shelf} > ${binData.position}`
    : locationData?.label || '';
  const headerCode = binData?.code || '';
  const headerIcon = scanType === 'bin' ? 'pozitie' : scanType === 'cab' ? 'dulap' : 'raft';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-testid="bin-article-selector">
        {/* Header */}
        <div className="bg-purple-50 border-b border-purple-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center shrink-0">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-slate-900">{headerLabel}</p>
              {headerCode && <p className="text-xs text-slate-500 font-mono">{headerCode}</p>}
            </div>
            <button onClick={onClose} className="p-1 hover:bg-purple-100 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          <Badge className="bg-purple-600 text-white mt-2">
            {articles.length} articole in aceasta {headerIcon}
          </Badge>
        </div>

        {/* Articles */}
        <div className="p-3 space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 140px)' }}>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-1">Selectati articolul:</p>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            articles.map((art) => (
              <button
                key={art.article_id}
                onClick={() => onSelect?.(art)}
                className="w-full flex items-center justify-between p-3 rounded-xl border-2 border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50 active:bg-blue-100 transition-all text-left"
                data-testid={`bin-art-${art.article_id}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                    <Package className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-slate-900 truncate">{art.article_name}</p>
                    <p className="text-xs text-slate-500 font-mono">{art.article_code}</p>
                    {art.supplier_name && (
                      <p className="text-[10px] text-slate-400 truncate">Furnizor: {art.supplier_name}</p>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0 ml-2">
                  <p className="text-lg font-black text-blue-700">{art.quantity_in_bin || art.quantity_in_location || 0}</p>
                  <p className="text-[10px] text-slate-500">{art.unit}</p>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Helper: Check if a QR scan result is a BIN/CAB/SHELF code and extract the value.
 * Returns { type: 'bin'|'cab'|'shelf', value: '...' } or { type: null }
 */
export const parseBinQrCode = (code) => {
  const parts = code.split(':');
  if (parts.length >= 2) {
    const prefix = parts[0].toUpperCase();
    const value = parts.slice(1).join(':');
    if (prefix === 'BIN') return { isBin: true, type: 'bin', value };
    if (prefix === 'CAB') return { isBin: true, type: 'cab', value };
    if (prefix === 'SHELF') return { isBin: true, type: 'shelf', value };
  }
  return { isBin: false, type: null };
};
